import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Star, Check, X, Info, Download, Save, FileJson, Mail, Antenna, ExternalLink, Copy, SlidersHorizontal, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { saveProject, newProjectId } from "@/lib/storage";
import { exportPdf } from "@/lib/pdfExport";
import LicenseConfigurator from "@/components/configurator/LicenseConfigurator";
import { buildCart, quantitiesFromRecommendation } from "@/lib/configurator";
import { shareProject } from "@/lib/api";

const EDITION_LABELS = {
  express: "XProtect Express+",
  professional: "XProtect Professional+",
  expert: "XProtect Expert",
  corporate: "XProtect Corporate",
};

// Kameramast recommendation based on cameras answer
const KAMERAMAST_HINTS = {
  lt_25: { heights: "4–6 m", note: "Standardmasten für kleine Areale, Parkplätze und Zufahrten." },
  "26_50": { heights: "4–8 m", note: "Kombinierte Mastlösungen mit Multi-Kamera-Aufsätzen." },
  "51_100": { heights: "6–8 m", note: "Mittlere Perimeterlösungen mit PTZ- und Bullet-Kameras." },
  "101_500": { heights: "6–10 m", note: "Verbundlösungen für Industrieareale und größere Perimeter." },
  gt_500: { heights: "8–12 m", note: "Enterprise-Perimeter mit Multi-Kamera-Masten und Failover-Verkabelung." },
};

function bucketFromExact(n) {
  if (!n || n <= 0) return null;
  if (n <= 25) return "lt_25";
  if (n <= 50) return "26_50";
  if (n <= 100) return "51_100";
  if (n <= 500) return "101_500";
  return "gt_500";
}

export default function RecommendationResult({ recommendation, answers, questions, projectName, setProjectName, projectId, features, onContact, initialConfig, initialCode, onOpenInConfigurator }) {
  const [saving, setSaving] = useState(false);
  const exactCount = Number(answers?.camerasExact) || 0;
  const defaultDevices = exactCount > 0 ? exactCount : 25;
  const [configState, setConfigState] = useState(
    initialConfig || { deviceCount: defaultDevices, carePackage: "carePlus", careDuration: "1" }
  );
  const [configCode, setConfigCode] = useState(initialCode || "");

  const kamBucket =
    (answers?.camerasExact && bucketFromExact(Number(answers.camerasExact))) ||
    answers?.cameras;

  if (!recommendation) {
    return <div className="text-muted-foreground">Empfehlung wird berechnet …</div>;
  }

  const rec = recommendation.recommendedEdition;
  const editionInfo = features?.editions?.[rec];
  const scores = recommendation.scores || {};

  const handleSave = async () => {
    if (!projectName.trim()) {
      toast.error("Bitte einen Projektnamen eingeben.");
      return;
    }
    setSaving(true);
    let code = configCode;
    try {
      const res = await shareProject({
        name: projectName.trim(),
        answers,
        recommendation: {
          recommendedEdition: recommendation?.recommendedEdition,
          recommendedEditionLabel: recommendation?.recommendedEditionLabel,
          scores: recommendation?.scores,
        },
        configuration: configState,
      });
      code = res?.code || code;
      setConfigCode(code);
    } catch (err) {
      toast.error("Konfigurationscode konnte nicht generiert werden.");
    }
    try {
      saveProject({
        id: projectId || newProjectId(),
        name: projectName.trim(),
        mode: "wizard",
        answers,
        recommendation,
        configuration: configState,
        code,
      });
      toast.success(`Projekt „${projectName.trim()}“ gespeichert.`);
    } catch (e) {
      toast.error("Lokales Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyCode = async () => {
    if (!configCode) return;
    try { await navigator.clipboard.writeText(configCode); toast.success("Konfigurationscode kopiert."); }
    catch { toast.error("Bitte manuell kopieren."); }
  };

  const handlePdf = async () => {
    try {
      const cart = buildCart({ edition: recommendation?.recommendedEdition, deviceCount: Number(configState.deviceCount) || 0, carePackage: configState.carePackage, careDuration: configState.careDuration });
      await exportPdf({
        projectName: projectName || "XProtect Empfehlung",
        answers,
        recommendation,
        features,
        questions,
        cart,
        configuration: configState,
        configCode,
      });
      toast.success("PDF wird generiert …");
    } catch (e) {
      console.error(e);
      toast.error("PDF-Export fehlgeschlagen.");
    }
  };

  const handleOpenInConfigurator = () => {
    if (!onOpenInConfigurator) return;
    const edition = recommendation?.recommendedEdition;
    const quantities = quantitiesFromRecommendation({
      edition,
      deviceCount: Number(configState.deviceCount) || 0,
      carePackage: configState.carePackage,
      careDuration: configState.careDuration,
    });
    onOpenInConfigurator({
      id: newProjectId(),
      name: projectName || "",
      mode: "configurator",
      source: "wizard",
      sourceEditionLabel: recommendation?.recommendedEditionLabel || EDITION_LABELS[edition],
      configuration: { mode: "configurator", edition, quantities },
      code: "",
    });
    toast.success("Empfehlung in den Konfigurator übernommen.");
  };

  const handleJson = () => {
    const payload = {
      projectName: projectName || null,
      exportedAt: new Date().toISOString(),
      answers,
      recommendation,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const safe = (projectName || "xprotect_empfehlung").replace(/[^a-zA-Z0-9-_ ]/g, "_");
    a.href = url;
    a.download = `${safe}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("JSON exportiert.");
  };

  return (
    <div data-testid="result-view" className="space-y-6">
      {/* Hero recommendation */}
      <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/10 to-primary/5 p-6 md:p-8">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
            <Star className="w-7 h-7 fill-current" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-1">Empfohlene Edition</div>
            <h2 data-testid="recommended-edition" className="text-2xl md:text-3xl font-bold">{editionInfo?.name || EDITION_LABELS[rec]}</h2>
            <p className="text-sm text-muted-foreground mt-1">{editionInfo?.tagline}</p>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Eignung</div>
            <div className="text-3xl font-bold text-primary tabular-nums">{scores[rec] || 0}%</div>
          </div>
        </div>

        {/* Score bars all editions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {Object.entries(scores).map(([ed, pct]) => (
            <div key={ed} className={cn("rounded-lg border p-3", ed === rec ? "border-primary bg-primary/10" : "border-border bg-secondary/40")}>
              <div className="text-xs text-muted-foreground">{EDITION_LABELS[ed]}</div>
              <div className="text-lg font-bold tabular-nums">{pct}%</div>
              <div className="h-1.5 bg-secondary rounded-full overflow-hidden mt-1">
                <div className={cn("h-full", ed === rec ? "bg-primary" : "bg-muted-foreground/50")} style={{ width: `${pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reasons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 border-border bg-card">
          <div className="flex items-center gap-2 mb-3">
            <Check className="w-5 h-5 text-emerald-500" />
            <h3 className="font-semibold">Warum diese Edition?</h3>
          </div>
          <ul className="space-y-2 text-sm">
            {recommendation.reasonsFor?.map((r, i) => (
              <li key={i} className="flex gap-2">
                <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="p-5 border-border bg-card">
          <div className="flex items-center gap-2 mb-3">
            <X className="w-5 h-5 text-rose-500" />
            <h3 className="font-semibold">Warum keine kleinere Edition?</h3>
          </div>
          <div className="space-y-4">
            {Object.entries(recommendation.reasonsAgainstSmaller || {}).length === 0 && (
              <p className="text-sm text-muted-foreground">Keine kleinere Edition unterhalb der empfohlenen.</p>
            )}
            {Object.entries(recommendation.reasonsAgainstSmaller || {}).map(([ed, reasons]) => (
              <div key={ed}>
                <div className="text-sm font-semibold mb-1">{EDITION_LABELS[ed]}</div>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {reasons.map((r, i) => (
                    <li key={i} className="flex gap-2">
                      <X className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 border-border bg-card md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Warum keine größere Edition?</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(recommendation.reasonsAgainstBigger || {}).length === 0 && (
              <p className="text-sm text-muted-foreground">Es gibt keine größere Edition oberhalb der Empfehlung.</p>
            )}
            {Object.entries(recommendation.reasonsAgainstBigger || {}).map(([ed, text]) => (
              <div key={ed} className="rounded-lg border border-border bg-secondary/40 p-3">
                <div className="text-sm font-semibold mb-1">{EDITION_LABELS[ed]}</div>
                <p className="text-sm text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>
        </Card>

        {recommendation.upgradeTriggers && Object.entries(recommendation.upgradeTriggers).length > 0 && (
          <Card className="p-5 border-border bg-card md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold">Was würde zu einer höheren Edition führen?</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(recommendation.upgradeTriggers).map(([ed, triggers]) => (
                <div key={ed} className="rounded-lg border border-border bg-secondary/40 p-3">
                  <div className="text-sm font-semibold mb-2">{EDITION_LABELS[ed]}</div>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {triggers.map((t, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-primary">→</span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Kameramast tie-in */}
      <Card data-testid="kameramast-hint" className="p-5 border-border bg-gradient-to-br from-primary/10 to-card overflow-hidden">
        <div className="flex flex-col md:flex-row items-start gap-5">
          <div className="w-40 shrink-0 flex items-center justify-center">
            <img src="/xortec/kameramasten-white.webp" alt="Xortec Kameramasten" className="w-full h-auto object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-primary font-semibold mb-1 flex items-center gap-1.5">
              <Antenna className="w-3.5 h-3.5" />
              Passende Kameramasten von Xortec
            </div>
            <h3 className="font-semibold text-lg mb-1">
              {kamBucket && KAMERAMAST_HINTS[kamBucket]
                ? `Empfohlene Masthöhen: ${KAMERAMAST_HINTS[kamBucket].heights}`
                : "Kameramasten passend zu Ihrer Installation"}
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              {kamBucket && KAMERAMAST_HINTS[kamBucket]
                ? KAMERAMAST_HINTS[kamBucket].note
                : "Xortec liefert eigene Kameramasten inkl. Blitzschutz, Verkabelung und Halterungen – passend zu Ihrer XProtect-Installation."}
              {" "}Gerne konfigurieren wir die passende Mastlösung zu Ihrer Empfehlung.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                asChild
                size="sm"
                variant="outline"
                className="border-primary/40"
                data-testid="btn-kameramast-brochure"
              >
                <a
                  href="https://xortec.de/media/23/25/72/1758812184/Xortec%20Kameramasten%20Broschre.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  Broschüre <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </Button>
              <Button
                size="sm"
                data-testid="btn-kameramast-contact"
                className="bg-primary text-primary-foreground"
                onClick={() =>
                  onContact &&
                  onContact({
                    projectName,
                    recommendedEditionLabel: recommendation?.recommendedEditionLabel,
                    scores: recommendation?.scores,
                    answers,
                    interest: "kameramast",
                  })
                }
              >
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Kameramast-Beratung
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Answers overview */}
      <Card className="p-5 border-border bg-card">
        <h3 className="font-semibold mb-3">Ihre Angaben</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {questions.map((q) => {
            const v = answers[q.key];
            const exact = q.exactField ? answers[q.exactField] : null;
            let disp = "—";
            if (exact && Number(exact) > 0) disp = `${exact} Kameras (exakt)`;
            else if (typeof v === "boolean") disp = v ? "Ja" : "Nein";
            else if (q.options) disp = q.options.find((o) => o.value === v)?.label || "—";
            else if (v && typeof v === "object") {
              const parts = Object.entries(v).filter(([, val]) => val).map(([k]) => q.subOptions.find((s) => s.value === k)?.label || k);
              disp = parts.length ? parts.join(", ") : "—";
            }
            return (
              <div key={q.key} className="flex justify-between gap-4 py-1 border-b border-border/60 last:border-none">
                <span className="text-muted-foreground">{q.label}</span>
                <span className="font-medium text-right">{disp}</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* License Configurator */}
      <LicenseConfigurator
        edition={recommendation?.recommendedEdition}
        defaultDeviceCount={defaultDevices}
        value={configState}
        onChange={setConfigState}
      />

      {onOpenInConfigurator && (
        <Card data-testid="handoff-configurator" className="p-5 border-primary/40 bg-primary/5">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="w-11 h-11 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
              <SlidersHorizontal className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-0.5">Weiter verfeinern</div>
              <h3 className="font-semibold">Empfehlung im Konfigurator öffnen</h3>
              <p className="text-sm text-muted-foreground">
                Übernimmt {EDITION_LABELS[rec]} mit Basis-Lizenz, {Number(configState.deviceCount) || 0} Device-Lizenzen
                {configState.carePackage !== "none" ? ` und ${configState.carePackage === "carePremium" ? "Care Premium" : "Care Plus"}` : ""}.
                Dort können Sie Mengen, Laufzeiten und Care-Stufen frei anpassen oder weitere Editionen ergänzen.
              </p>
            </div>
            <Button
              data-testid="btn-open-in-configurator"
              onClick={handleOpenInConfigurator}
              className="bg-primary text-primary-foreground shrink-0"
            >
              Im Konfigurator verfeinern <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="result-project-name"
          placeholder="Projektname (zum Speichern)"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="w-64 bg-secondary/40 border-border"
        />
        <Button data-testid="btn-save-project" onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground">
          <Save className="w-4 h-4 mr-1" /> Projekt speichern
        </Button>
        <Button data-testid="btn-export-pdf" variant="outline" onClick={handlePdf}>
          <Download className="w-4 h-4 mr-1" /> PDF exportieren
        </Button>
        <Button data-testid="btn-export-json" variant="outline" onClick={handleJson}>
          <FileJson className="w-4 h-4 mr-1" /> JSON exportieren
        </Button>
        <Button
          data-testid="btn-contact-offer"
          variant="outline"
          className="border-primary/40 text-foreground hover:bg-primary/10"
          onClick={() =>
            onContact &&
            onContact({
              projectName,
              recommendedEditionLabel: recommendation?.recommendedEditionLabel,
              scores: recommendation?.scores,
              answers,
            })
          }
        >
          <Mail className="w-4 h-4 mr-1" /> Angebot anfragen
        </Button>
      </div>

      {configCode && (
        <Card data-testid="config-code-card" className="p-5 border-primary/40 bg-primary/5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Konfigurationscode</div>
              <p className="text-sm text-muted-foreground mb-3">
                Mit diesem 6-stelligen Code können Sie Ihr Projekt später wieder öffnen – auch in einem anderen Browser.
              </p>
              <div className="inline-flex items-center gap-2 bg-background/80 border border-primary/40 rounded-lg px-4 py-3">
                <span className="font-mono text-3xl md:text-4xl font-bold tracking-[0.35em] text-primary select-all">
                  {configCode}
                </span>
                <Button size="sm" variant="ghost" onClick={handleCopyCode} data-testid="btn-copy-code" className="ml-1">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
